import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import PublicSharePage from '../../pages/PublicSharePage';
import './ShareLinkEditor.css';

/**
 * Brand settings → Public results links → New / Edit link.
 *
 * Everything prospects will see is set here before the link exists: which
 * strategies and their sizes, the order and color of each line, which market
 * comparisons are offered and which show at the start, the starting capital
 * and period, the page's accent color, intro and sections. The right-hand side
 * is the real public page for this draft (POST /api/share-links/preview),
 * rebuilt a moment after each change.
 */

const MAX_STRATEGIES = 6;
const usdShort = (n) => `$${(n / 1000).toLocaleString('en-US')}k`;

const json = () => ({ ...getBearerAuthHeaders(), 'Content-Type': 'application/json' });
async function post(url, body, method = 'POST') {
  const res = await fetch(url, { method, headers: json(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || 'Something went wrong.');
  return data;
}

function ColorInput({ value, onChange, label }) {
  return (
    <input
      type="color"
      className="sle-color"
      value={value}
      aria-label={label}
      title={label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Page sections a new link copies from the brand's most recent link. */
const SECTION_KEYS = ['showMonthly', 'showIncluded', 'showTrades', 'showJoin'];

/**
 * The draft as the server stores it, from an existing link, or for a new one
 * empty apart from the sections, which follow `previous` (the brand's latest
 * link). The RiskLo section starts off on every new link; it can be turned on.
 */
export function initialDraft(link, options, previous = null) {
  const s = link?.settings
    || { ...Object.fromEntries(SECTION_KEYS.map((k) => [k, previous?.settings?.[k]]).filter(([, v]) => typeof v === 'boolean')), showPromo: false };
  return {
    title: link?.title || '',
    picked: (link?.strategies || []).map((x) => ({ name: x.name, contracts: String(x.contracts ?? 1) })),
    colors: { ...(s.colors || {}) },
    initialStrategies: s.initialStrategies || [],
    benchmarks: s.benchmarks || ['SPY', 'QQQ'],
    initialBenchmarks: s.initialBenchmarks || ['SPY'],
    benchmarkColors: { ...Object.fromEntries((options?.benchmarks || []).map((b) => [b.id, b.color])), ...(s.benchmarkColors || {}) },
    defaultCapital: s.defaultCapital || 100000,
    defaultRange: s.defaultRange || 'All',
    accentColor: s.accentColor || '',
    intro: s.intro || '',
    showMonthly: s.showMonthly !== false,
    showIncluded: s.showIncluded !== false,
    showPromo: s.showPromo !== false,
    showJoin: s.showJoin !== false,
    showTrades: s.showTrades !== false,
    // 'brand' = the brand's own colors, fonts and layout style; 'standard' = RiskLo's look.
    look: s.look === 'standard' ? 'standard' : 'brand',
    showBacktested: Boolean(link?.showBacktested),
    emailGate: Boolean(link?.emailGate),
    expires: '',
  };
}

export default function ShareLinkEditor({ link = null, previous = null, brandName = '', strategies, unavailable = [], options, onSaved, onCancel }) {
  const editing = Boolean(link);
  const [d, setD] = useState(() => initialDraft(link, options, previous));
  const set = (patch) => setD((cur) => ({ ...cur, ...patch }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);

  const palette = options?.palette || ['#3b82f6', '#a855f7', '#22c55e', '#06b6d4', '#ec4899', '#84cc16'];
  const benchOptions = options?.benchmarks || [];
  const capitals = options?.capitals || [25000, 50000, 100000, 250000];
  const ranges = options?.ranges || ['1M', '3M', '6M', 'YTD', '1Y', 'All'];
  const labelOf = (name) => strategies.find((s) => s.name === name)?.displayName || name;
  const pickedNames = d.picked.map((p) => p.name);
  const colorOf = (name) => d.colors[name] || palette[Math.max(0, pickedNames.indexOf(name)) % palette.length];
  const initialStrategies = d.initialStrategies.filter((n) => pickedNames.includes(n));
  const effectiveInitial = initialStrategies.length ? initialStrategies : pickedNames.slice(0, 1);
  const anyLive = strategies.some((s) => pickedNames.includes(s.name) && s.liveSince);

  const toggleStrategy = (name) => {
    if (pickedNames.includes(name)) {
      set({ picked: d.picked.filter((p) => p.name !== name) });
    } else if (d.picked.length < MAX_STRATEGIES) {
      set({ picked: [...d.picked, { name, contracts: '1' }] });
    }
  };
  const move = (name, dir) => {
    const i = pickedNames.indexOf(name);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= d.picked.length) return;
    const next = [...d.picked];
    [next[i], next[j]] = [next[j], next[i]];
    set({ picked: next });
  };
  const toggleIn = (key, id) => set({ [key]: d[key].includes(id) ? d[key].filter((x) => x !== id) : [...d[key], id] });

  // What the server stores; also what the preview builds from.
  const body = useMemo(() => ({
    title: d.title.trim() || 'Untitled link',
    strategies: d.picked.map((p) => ({ name: p.name, contracts: Number(p.contracts) || 1 })),
    showBacktested: d.showBacktested,
    emailGate: d.emailGate,
    settings: {
      colors: Object.fromEntries(pickedNames.map((n) => [n, colorOf(n)])),
      initialStrategies: effectiveInitial,
      benchmarks: d.benchmarks,
      initialBenchmarks: d.initialBenchmarks.filter((id) => d.benchmarks.includes(id)),
      benchmarkColors: d.benchmarkColors,
      defaultCapital: d.defaultCapital,
      defaultRange: d.defaultRange,
      accentColor: d.accentColor || null,
      intro: d.intro,
      showMonthly: d.showMonthly,
      showIncluded: d.showIncluded,
      showPromo: d.showPromo,
      showJoin: d.showJoin,
      showTrades: d.showTrades,
      look: d.look,
    },
  // colorOf/effectiveInitial derive from d.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [d]);

  // Live preview, a moment after the last change. Only the latest answer is shown.
  const seq = useRef(0);
  useEffect(() => {
    if (!body.strategies.length) { setPreview(null); setPreviewError(''); return undefined; }
    const mine = ++seq.current;
    setPreviewBusy(true);
    const t = setTimeout(() => {
      post(API_ENDPOINTS.shareLinkPreview, body)
        .then((data) => { if (mine === seq.current) { setPreview(data.view); setPreviewError(''); } })
        .catch((err) => { if (mine === seq.current) setPreviewError(err.message); })
        .finally(() => { if (mine === seq.current) setPreviewBusy(false); });
    }, 600);
    return () => clearTimeout(t);
  }, [body]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const payload = { ...body, title: d.title };
      const data = editing
        ? await post(API_ENDPOINTS.shareLink(link.id), payload, 'PATCH')
        : await post(API_ENDPOINTS.shareLinks, { ...payload, expiresInDays: d.expires ? Number(d.expires) : null });
      onSaved(data.link);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="sle" onSubmit={submit}>
      <div className="sle-settings">
        <h4>{editing ? 'Edit link' : 'New link'}</h4>

        <fieldset className="sle-group">
          <legend>Page</legend>
          <label className="bs-field"><span>Title prospects see</span><input value={d.title} maxLength={120} placeholder={`e.g. ${String(brandName || '').trim() || 'Your brand'}: live results`} onChange={(e) => set({ title: e.target.value })} /></label>
          <label className="bs-field"><span>Intro (optional)</span><textarea rows={2} maxLength={400} value={d.intro} placeholder="A sentence or two under the title." onChange={(e) => set({ intro: e.target.value })} /></label>
          <div className="sle-row">
            <label className="sle-inline">
              <input type="checkbox" checked={Boolean(d.accentColor)} onChange={(e) => set({ accentColor: e.target.checked ? '#3b82f6' : '' })} />
              Accent color
            </label>
            {d.accentColor ? <ColorInput value={d.accentColor} onChange={(v) => set({ accentColor: v })} label="Accent color" /> : <small className="sle-hint">Your brand’s color</small>}
          </div>
          <div className="sle-look" role="radiogroup" aria-label="Look">
            <span className="sle-look-label">Look</span>
            <label className="sle-inline"><input type="radio" name={`sle-look-${link?.id || "new"}`} checked={d.look === 'brand'} onChange={() => set({ look: 'brand' })} /> Brand theme <small className="sle-hint">your colors, fonts and layout from Colors &amp; layout</small></label>
            <label className="sle-inline"><input type="radio" name={`sle-look-${link?.id || "new"}`} checked={d.look === 'standard'} onChange={() => set({ look: 'standard' })} /> RiskLo standard</label>
          </div>
          <div className="sle-checks">
            <label className="sle-inline"><input type="checkbox" checked={d.showMonthly} onChange={(e) => set({ showMonthly: e.target.checked })} /> Monthly returns</label>
            <label className="sle-inline"><input type="checkbox" checked={d.showIncluded} onChange={(e) => set({ showIncluded: e.target.checked })} /> What’s included</label>
            <label className="sle-inline"><input type="checkbox" checked={d.showTrades} onChange={(e) => set({ showTrades: e.target.checked })} /> List of trades</label>
            <label className="sle-inline"><input type="checkbox" checked={d.showJoin} onChange={(e) => set({ showJoin: e.target.checked })} /> “Join” button</label>
            <label className="sle-inline"><input type="checkbox" checked={d.showPromo} onChange={(e) => set({ showPromo: e.target.checked })} /> RiskLo section (“Explore RiskLo”)</label>
          </div>
        </fieldset>

        <fieldset className="sle-group">
          <legend>Strategies (up to {MAX_STRATEGIES})</legend>
          <p className="sle-hint">Each one is its own line. Size: 1 = one NQ, 0.1 = one MNQ. “At start” is what shows when the page opens; prospects can turn the others on.</p>
          {strategies.length ? (
            <ul className="sle-list">
              {strategies.map((s) => {
                const on = pickedNames.includes(s.name);
                const p = d.picked.find((x) => x.name === s.name);
                const i = pickedNames.indexOf(s.name);
                return (
                  <li key={s.name} className={on ? 'is-on' : ''}>
                    <label className="sle-name">
                      <input type="checkbox" checked={on} disabled={!on && d.picked.length >= MAX_STRATEGIES} onChange={() => toggleStrategy(s.name)} />
                      <span>{s.displayName || s.name}<small>{s.liveSince ? `Live since ${s.liveSince}` : 'Full history'}</small></span>
                    </label>
                    {on ? (
                      <div className="sle-line">
                        <ColorInput value={colorOf(s.name)} onChange={(v) => set({ colors: { ...d.colors, [s.name]: v } })} label={`Line color for ${labelOf(s.name)}`} />
                        <input type="number" min="0.1" max="100" step="0.1" value={p.contracts} aria-label={`Size for ${labelOf(s.name)}`} onChange={(e) => set({ picked: d.picked.map((x) => (x.name === s.name ? { ...x, contracts: e.target.value } : x)) })} />
                        <label className="sle-inline" title="Shown when the page opens">
                          <input type="checkbox" checked={effectiveInitial.includes(s.name)} onChange={() => toggleIn('initialStrategies', s.name)} /> At start
                        </label>
                        <span className="sle-order">
                          <button type="button" onClick={() => move(s.name, -1)} disabled={i <= 0} aria-label={`Move ${labelOf(s.name)} up`}>↑</button>
                          <button type="button" onClick={() => move(s.name, 1)} disabled={i >= d.picked.length - 1} aria-label={`Move ${labelOf(s.name)} down`}>↓</button>
                        </span>
                      </div>
                    ) : null}
                  </li>
                );
              })}
              {unavailable.map((s) => (
                <li key={s.name} className="is-unavailable" title="Turn on “Visible to members” on the Strategies page to share it">
                  <label className="sle-name">
                    <input type="checkbox" disabled />
                    <span>{s.displayName || s.name}<small>{s.reason}: turn on “Visible to members” on Strategies to share it</small></span>
                  </label>
                </li>
              ))}
            </ul>
          ) : <p className="sle-hint">No public strategies yet. Turn on “Visible to members” on the Strategies page first.</p>}
        </fieldset>

        <fieldset className="sle-group">
          <legend>Compare with markets</legend>
          <p className="sle-hint">Offered: prospects can turn these on and off (dashed lines). At start: shown when the page opens.</p>
          <ul className="sle-list">
            {benchOptions.map((b) => {
              const on = d.benchmarks.includes(b.id);
              return (
                <li key={b.id} className={on ? 'is-on' : ''}>
                  <label className="sle-name">
                    <input type="checkbox" checked={on} onChange={() => toggleIn('benchmarks', b.id)} />
                    <span>{b.label}</span>
                  </label>
                  {on ? (
                    <div className="sle-line">
                      <ColorInput value={d.benchmarkColors[b.id] || b.color} onChange={(v) => set({ benchmarkColors: { ...d.benchmarkColors, [b.id]: v } })} label={`Line color for ${b.label}`} />
                      <label className="sle-inline"><input type="checkbox" checked={d.initialBenchmarks.includes(b.id)} onChange={() => toggleIn('initialBenchmarks', b.id)} /> At start</label>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </fieldset>

        <fieldset className="sle-group">
          <legend>When the page opens</legend>
          <div className="sle-row">
            <label className="bs-field"><span>Starting capital</span>
              <select value={d.defaultCapital} onChange={(e) => set({ defaultCapital: Number(e.target.value) })}>
                {capitals.map((c) => <option key={c} value={c}>{usdShort(c)}</option>)}
              </select>
            </label>
            <label className="bs-field"><span>Period</span>
              <select value={d.defaultRange} onChange={(e) => set({ defaultRange: e.target.value })}>
                {ranges.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="sle-group">
          <legend>Access</legend>
          <label className="shl-toggle">
            <input type="checkbox" checked={d.showBacktested} onChange={(e) => set({ showBacktested: e.target.checked })} />
            <span>
              <strong>Include backtested results</strong>
              <small>Adds Backtested and Full Journey tabs for strategies with a live-since date, with a hypothetical-results disclaimer. Off: live results only.{!anyLive && d.picked.length ? ' None of the picked strategies has a live-since date, so the full history is shown either way.' : ''}</small>
            </span>
          </label>
          <label className="shl-toggle">
            <input type="checkbox" checked={d.emailGate} onChange={(e) => set({ emailGate: e.target.checked })} />
            <span>
              <strong>Ask for an email first</strong>
              <small>Prospects enter their email to see the results; you’ll see the list here.</small>
            </span>
          </label>
          {!editing ? (
            <label className="bs-field shl-expires">
              <span>Expires</span>
              <select value={d.expires} onChange={(e) => set({ expires: e.target.value })}>
                <option value="">Never</option>
                <option value="7">In 7 days</option>
                <option value="30">In 30 days</option>
                <option value="90">In 90 days</option>
              </select>
            </label>
          ) : null}
        </fieldset>

        {error ? <p className="bs-note is-bad" role="alert">{error}</p> : null}
        <div className="bs-actions sle-actions">
          <button type="submit" className="bs-btn bs-btn-primary" disabled={busy || !d.title.trim() || !d.picked.length}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create link'}
          </button>
          <button type="button" className="bs-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>

      <div className="sle-preview">
        <div className="sle-preview-head">
          <strong>Preview</strong>
          <span>{previewBusy ? 'Updating…' : 'What prospects will see'}</span>
        </div>
        <div className="sle-preview-frame">
          {!d.picked.length ? <p className="sle-hint sle-preview-empty">Pick a strategy to see the page.</p> : null}
          {d.picked.length && previewError ? <p className="bs-note is-bad">{previewError}</p> : null}
          {d.picked.length && preview ? <PublicSharePage previewView={preview} /> : null}
        </div>
      </div>
    </form>
  );
}
